import { createContext, useContext, useState, useEffect, ReactNode, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { announcementService } from '../services/announcementService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Trade, TradingAccount } from '../types';
import type { AccountStats } from '../services/accountStatsService';

const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const roundTo = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type DemoInstrument = {
  asset: string;
  basePrice: number;
  volatility: number;
  lotSizes: number[];
  timeframes: string[];
  sessions: string[];
  platform: Trade['platform'];
};

const DEMO_INSTRUMENTS: DemoInstrument[] = [
  {
    asset: 'EURUSD',
    basePrice: 1.0845,
    volatility: 0.0045,
    lotSizes: [0.25, 0.5, 0.75, 1],
    timeframes: ['M15', 'M30', 'H1'],
    sessions: ['London Open', 'London', 'New York Open'],
    platform: 'mt5'
  },
  {
    asset: 'GBPUSD',
    basePrice: 1.271,
    volatility: 0.006,
    lotSizes: [0.2, 0.4, 0.6, 0.8],
    timeframes: ['M15', 'H1', 'H4'],
    sessions: ['London', 'London Close', 'New York'],
    platform: 'mt5'
  },
  {
    asset: 'USDJPY',
    basePrice: 151.35,
    volatility: 0.55,
    lotSizes: [0.2, 0.4, 0.6, 1],
    timeframes: ['M5', 'M15', 'H1'],
    sessions: ['Asia', 'London Open', 'New York'],
    platform: 'mt4'
  },
  {
    asset: 'XAUUSD',
    basePrice: 2342,
    volatility: 18,
    lotSizes: [0.05, 0.1, 0.2, 0.3],
    timeframes: ['M15', 'H1', 'H4'],
    sessions: ['London', 'New York Open', 'New York'],
    platform: 'mt5'
  },
  {
    asset: 'BTCUSD',
    basePrice: 64250,
    volatility: 950,
    lotSizes: [0.01, 0.02, 0.03, 0.05],
    timeframes: ['M15', 'H1', 'H4'],
    sessions: ['London', 'New York', 'US Close'],
    platform: 'ctrader'
  }
];

const DEMO_SETUPS = [
  'Breakout clean',
  'Pullback tendance',
  'Range rejection',
  'Liquidity sweep',
  'News fade'
];

const DEMO_CONDITIONS = [
  'Confluence HTF',
  'Risque défini',
  'Entrée sur zone',
  'Timing session OK',
  'Plan respecté'
];

const DEMO_MOODS: Trade['mood'][] = ['CONFIDENT', 'NEUTRAL', 'STRESSED', 'FOMO', 'REVENGE', 'TIRED'];

const generateMockTrades = (): Trade[] => {
  const trades: Trade[] = [];
  const now = new Date();
  let runningBalance = 10000;
  let tradeId = 1;

  for (let monthOffset = 5; monthOffset >= 0; monthOffset--) {
    const tradesInMonth = 10 + Math.floor(seededRandom(monthOffset * 100) * 8);

    for (let index = 0; index < tradesInMonth; index++) {
      const seed = monthOffset * 1000 + index;
      const instrument = DEMO_INSTRUMENTS[Math.floor(seededRandom(seed + 1) * DEMO_INSTRUMENTS.length)];
      const direction: Trade['type'] = seededRandom(seed + 2) > 0.48 ? 'LONG' : 'SHORT';
      const monthDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
      const day = 1 + Math.floor(seededRandom(seed + 3) * 27);
      const hour = 7 + Math.floor(seededRandom(seed + 4) * 11);
      const minute = Math.floor(seededRandom(seed + 5) * 60);
      const openDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day, hour, minute);
      const durationMinutes = 25 + Math.floor(seededRandom(seed + 6) * 360);
      const closeDate = new Date(openDate.getTime() + durationMinutes * 60 * 1000);

      const priceNoise = (seededRandom(seed + 7) - 0.5) * instrument.volatility;
      const entryPrice = roundTo(instrument.basePrice + priceNoise, instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5);
      const riskDistance = instrument.volatility * (0.18 + seededRandom(seed + 8) * 0.28);
      const plannedRR = roundTo(1.2 + seededRandom(seed + 9) * 2.2, 2);

      const stopLoss = direction === 'LONG'
        ? roundTo(entryPrice - riskDistance, instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5)
        : roundTo(entryPrice + riskDistance, instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5);

      const takeProfitDistance = riskDistance * plannedRR;
      const takeProfit = direction === 'LONG'
        ? roundTo(entryPrice + takeProfitDistance, instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5)
        : roundTo(entryPrice - takeProfitDistance, instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5);

      const riskPercentage = roundTo(0.35 + seededRandom(seed + 10) * 1.25, 2);
      const grossRiskAmount = runningBalance * (riskPercentage / 100);

      const outcomeRoll = seededRandom(seed + 11);
      let realizedRR: number;
      let outcome: Trade['outcome'];
      if (outcomeRoll < 0.56) {
        realizedRR = roundTo(0.6 + seededRandom(seed + 12) * plannedRR, 2);
        outcome = 'WIN';
      } else if (outcomeRoll < 0.92) {
        realizedRR = -roundTo(0.35 + seededRandom(seed + 13) * 0.9, 2);
        outcome = 'LOSS';
      } else {
        realizedRR = roundTo((seededRandom(seed + 14) - 0.5) * 0.08, 2);
        outcome = 'BREAK_EVEN';
      }

      const commission = roundTo(-(1.2 + seededRandom(seed + 15) * 4.8), 2);
      const swap = roundTo((seededRandom(seed + 16) - 0.35) * 1.4, 2);
      const pricePrecision = instrument.asset.includes('JPY') ? 3 : instrument.asset === 'XAUUSD' || instrument.asset === 'BTCUSD' ? 2 : 5;
      const exitPrice = roundTo(
        entryPrice + (direction === 'LONG' ? 1 : -1) * riskDistance * realizedRR,
        pricePrecision
      );
      const profitLoss = roundTo(grossRiskAmount * realizedRR + commission + swap, 2);
      runningBalance = roundTo(runningBalance + profitLoss, 2);
      const setup = DEMO_SETUPS[Math.floor(seededRandom(seed + 17) * DEMO_SETUPS.length)];
      const mood = DEMO_MOODS[Math.floor(seededRandom(seed + 18) * DEMO_MOODS.length)];
      const session = instrument.sessions[Math.floor(seededRandom(seed + 19) * instrument.sessions.length)];
      const timeframe = instrument.timeframes[Math.floor(seededRandom(seed + 20) * instrument.timeframes.length)];
      const lotSize = instrument.lotSizes[Math.floor(seededRandom(seed + 21) * instrument.lotSizes.length)];
      const conditions = DEMO_CONDITIONS.filter((_, conditionIndex) => seededRandom(seed + 30 + conditionIndex) > 0.35).slice(0, 4);

      const trade: Trade = {
        id: `demo-trade-${tradeId}`,
        accountId: 'demo-account',
        userId: 'demo-user',
        timestamp: closeDate.toISOString(),
        date: formatDateKey(openDate),
        type: direction,
        direction,
        asset: instrument.asset,
        symbol: instrument.asset,
        entryPrice,
        openPrice: entryPrice,
        exitPrice,
        closePrice: exitPrice,
        stopLoss,
        takeProfit,
        lotSize,
        volume: lotSize,
        riskPercentage,
        riskRewardRatio: `1:${Math.abs(realizedRR).toFixed(2)}`,
        plannedRiskRewardRatio: `1:${plannedRR.toFixed(2)}`,
        profitLoss,
        commission,
        swap,
        runningBalance,
        mood,
        outcome,
        notes: `${setup} sur ${instrument.asset} pendant ${session.toLowerCase()}. Exécution disciplinée et gestion cohérente du risque.`,
        conditions,
        platform: instrument.platform,
        timeframe,
        accountCurrency: 'EUR',
        openDateTime: openDate.toISOString(),
        closeDateTime: closeDate.toISOString(),
        order_details: {
          order: {
            stopLoss,
            takeProfit
          }
        },
        setups: [setup],
        setup,
        session,
        includedInStats: true,
        ticketId: `D-${10000 + tradeId}`
      };

      trades.push(trade);
      tradeId++;
    }
  }

  return trades.sort((left, right) => new Date(left.closeDateTime || left.timestamp).getTime() - new Date(right.closeDateTime || right.timestamp).getTime());
};

// Données de démo pré-générées (constantes)
const DEMO_TRADES = generateMockTrades();
const DEMO_ACCOUNT: TradingAccount = {
  id: 'demo-account',
  name: 'Compte Démo',
  currency: 'EUR',
  initialBalance: 10000,
  currentBalance: roundTo(DEMO_TRADES[DEMO_TRADES.length - 1]?.runningBalance || 10000, 2),
  userId: 'demo-user',
  type: 'demo',
  createdAt: new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1).toISOString(),
  updatedAt: new Date().toISOString(),
  platform: 'mt5'
};

// Calculer les stats de démo
const calculateDemoStats = (trades: Trade[], initialBalance: number): AccountStats => {
  const winningTrades = trades.filter(t => (t.profitLoss || 0) > 0);
  const losingTrades = trades.filter(t => (t.profitLoss || 0) < 0);
  const breakEvenTrades = trades.filter(t => t.profitLoss === 0);
  const totalProfit = trades.reduce((sum, t) => sum + (t.profitLoss || 0), 0);
  const profits = winningTrades.reduce((sum, t) => sum + (t.profitLoss || 0), 0);
  const losses = Math.abs(losingTrades.reduce((sum, t) => sum + (t.profitLoss || 0), 0));
  const currentBalance = trades[trades.length - 1]?.runningBalance || initialBalance;
  const maxBalance = trades.reduce((max, trade) => Math.max(max, trade.runningBalance || initialBalance), initialBalance);
  const averageRiskReward = trades.reduce((sum, trade) => {
    if (!trade.riskRewardRatio) return sum;
    const ratio = typeof trade.riskRewardRatio === 'string' && trade.riskRewardRatio.includes(':')
      ? parseFloat(trade.riskRewardRatio.split(':')[1])
      : Number(trade.riskRewardRatio);
    return sum + (Number.isFinite(ratio) ? ratio : 0);
  }, 0) / Math.max(trades.length, 1);

  let peakBalance = initialBalance;
  let maxDrawdown = 0;
  trades.forEach(trade => {
    const balance = trade.runningBalance || initialBalance;
    peakBalance = Math.max(peakBalance, balance);
    const drawdown = peakBalance > 0 ? ((peakBalance - balance) / peakBalance) * 100 : 0;
    maxDrawdown = Math.max(maxDrawdown, drawdown);
  });
  
  return {
    totalTrades: trades.length,
    winningTradesCount: winningTrades.length,
    losingTradesCount: losingTrades.length,
    breakEvenTradesCount: breakEvenTrades.length,
    winRate: Math.round((winningTrades.length / trades.length) * 100 * 10) / 10,
    totalProfit: Math.round(totalProfit * 100) / 100,
    totalReturnPercentage: Math.round((totalProfit / initialBalance) * 100 * 10) / 10,
    currentBalance: roundTo(currentBalance, 2),
    maxBalance: roundTo(maxBalance, 2),
    profitFactor: {
      profits: Math.round(profits * 100) / 100,
      losses: -Math.round(losses * 100) / 100
    },
    averageRiskReward: roundTo(averageRiskReward, 2),
    maxDrawdown: roundTo(maxDrawdown, 2),
    averageTradeReturn: Math.round((totalProfit / trades.length) * 100) / 100,
    dataQuality: {
      score: 100,
      status: 'complete' as const,
      completedFieldsPercentage: 100
    },
    lastUpdated: new Date()
  };
};

const DEMO_STATS = calculateDemoStats(DEMO_TRADES, DEMO_ACCOUNT.initialBalance);

export interface DemoData {
  trades: Trade[];
  account: TradingAccount;
  stats: AccountStats;
}

export function getDemoPreviewData(): DemoData {
  return {
    trades: DEMO_TRADES,
    account: DEMO_ACCOUNT,
    stats: DEMO_STATS
  };
}

export type TrialAnnouncementType = 'activation' | 'day-1' | 'day-2' | 'day-3' | 'trial-expired' | null;

export interface TrialAnnouncement {
  type: TrialAnnouncementType;
  message: React.ReactNode;
  mobileMessage?: React.ReactNode;
  backgroundColor: string;
  lightBackgroundColor?: string;
  color: string;
  iconColor: string;
  icon: JSX.Element;
  buttonText?: string;
  mobileButtonText?: string;
  buttonAction?: string;
  canDismiss: boolean;
  remainingTime?: number; // en millisecondes
  discountLabel?: string;
}

interface AnnouncementContextType {
  showLightModeModal: boolean;
  setShowLightModeModal: (show: boolean) => void;
  dismissLightModeAnnouncement: () => Promise<void>;
  markLightModeAnnouncementAsSeen: () => Promise<void>;
  showColorThemeModal: boolean;
  setShowColorThemeModal: (show: boolean) => void;
  dismissColorThemeAnnouncement: () => Promise<void>;
  markColorThemeAnnouncementAsSeen: () => Promise<void>;
  hideMainLayout: boolean;
  demoData: DemoData | null;
  trialAnnouncement: TrialAnnouncement | null;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

export function AnnouncementProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [showLightModeModal, setShowLightModeModal] = useState(false);
  const [showColorThemeModal, setShowColorThemeModal] = useState(false);
  const [checkedUserIds, setCheckedUserIds] = useState<Set<string>>(new Set());
  const [trialAnnouncement, setTrialAnnouncement] = useState<TrialAnnouncement | null>(null);
  
  // Stocker si l'utilisateur doit voir le modal de couleurs (pour l'afficher après le modal light)
  const pendingColorThemeModal = useRef(false);
  // Tracker si le modal light a été complété (fermé par l'utilisateur)
  const lightModeModalCompleted = useRef(false);

  // Données trial stockées en mémoire (une seule lecture Firestore)
  const trialDataRef = useRef<{ isTrial: boolean; endDate: Date | null; homePromo: boolean }>({ isTrial: false, endDate: null, homePromo: false });

  // Charger les données trial UNE SEULE FOIS depuis Firestore
  useEffect(() => {
    if (!user?.uid) {
      trialDataRef.current = { isTrial: false, endDate: null, homePromo: false };
      setTrialAnnouncement(null);
      return;
    }

    let cancelled = false;
    const loadTrialData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (cancelled || !userDoc.exists()) return;

        const userData = userDoc.data();
        
        if (userData.isTrial === true && userData.premiumEndDate) {
          trialDataRef.current = {
            isTrial: true,
            endDate: userData.premiumEndDate.toDate(),
            homePromo: userData.homePromo === true
          };
        } else {
          trialDataRef.current = { isTrial: false, endDate: null, homePromo: false };
          setTrialAnnouncement(null);
        }
      } catch (error) {
        // Déconnexion pendant la requête : le permission-denied est attendu, pas un bug.
        if (cancelled) return;
        console.error('Erreur lors du chargement du statut trial:', error);
      }
    };

    loadTrialData();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Mettre à jour le countdown LOCALEMENT (pas de requête Firestore)
  useEffect(() => {
    const updateCountdown = () => {
      const { isTrial, endDate } = trialDataRef.current;
      
      if (!isTrial || !endDate) {
        setTrialAnnouncement(null);
        return;
      }

      const now = new Date();
      const remainingMs = endDate.getTime() - now.getTime();
      const remainingHours = remainingMs / (1000 * 60 * 60);

      let announcement: TrialAnnouncement | null = null;

      if (remainingHours <= 0) {
        // Trial expiré - Afficher un message avec icône de cadenas
        const LockIcon = () => (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path fillRule="evenodd" clipRule="evenodd" d="M5.25 10.0546V8C5.25 4.27208 8.27208 1.25 12 1.25C15.7279 1.25 18.75 4.27208 18.75 8V10.0546C19.8648 10.1379 20.5907 10.348 21.1213 10.8787C22 11.7574 22 13.1716 22 16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H8C5.17157 22 3.75736 22 2.87868 21.1213C2 20.2426 2 18.8284 2 16C2 13.1716 2 11.7574 2.87868 10.8787C3.40931 10.348 4.13525 10.1379 5.25 10.0546ZM6.75 8C6.75 5.10051 9.10051 2.75 12 2.75C14.8995 2.75 17.25 5.10051 17.25 8V10.0036C16.867 10 16.4515 10 16 10H8C7.54849 10 7.13301 10 6.75 10.0036V8ZM12 13.25C12.4142 13.25 12.75 13.5858 12.75 14V18C12.75 18.4142 12.4142 18.75 12 18.75C11.5858 18.75 11.25 18.4142 11.25 18V14C11.25 13.5858 11.5858 13.25 12 13.25Z" fill="currentColor"></path>
            </g>
          </svg>
        );

        announcement = {
          type: 'trial-expired',
          message: <>Ton essai est terminé. Tes erreurs sont toujours détectées — le détail est masqué.</>,
          mobileMessage: "Essai terminé",
          backgroundColor: '#ef44446b',
          lightBackgroundColor: '#ef4444cf',
          color: '#ffffff',
          iconColor: '#fca5a5',
          icon: <LockIcon />,
          buttonText: 'Débloquer mon analyse',
          mobileButtonText: 'Débloquer',
          buttonAction: 'pricing-modal-expired',
          canDismiss: false,
          remainingTime: 0,
        };
      } else if (remainingHours <= 24) {
        // J-3: Dernières 24h - Rouge avec chrono
        const WarningIcon = () => (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
            <path fillRule="evenodd" clipRule="evenodd" d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615ZM12 7.25C12.4142 7.25 12.75 7.58579 12.75 8V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V8C11.25 7.58579 11.5858 7.25 12 7.25ZM12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z" fill="currentColor"/>
          </svg>
        );

        const hours = Math.floor(remainingMs / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
        const countdown = `${hours}h ${minutes}m ${seconds}s`;

        announcement = {
          type: 'day-3',
          message: <>Ton essai se termine dans {countdown}. Sécurise ton accès maintenant.</>,
          mobileMessage: countdown,
          backgroundColor: '#ef44446b',
          lightBackgroundColor: '#ef4444cf',
          color: '#ffffff',
          iconColor: '#fca5a5',
          icon: <WarningIcon />,
          buttonText: 'Garder mon accès',
          mobileButtonText: 'Sécuriser',
          buttonAction: 'pricing-modal-urgent',
          canDismiss: false,
          remainingTime: remainingMs,
        };
      } else if (remainingHours <= 48) {
        // J-2: 24-48h - Orange
        const AlertIcon = () => (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
            <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12ZM12 6.25C12.4142 6.25 12.75 6.58579 12.75 7V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V7C11.25 6.58579 11.5858 6.25 12 6.25ZM12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z" fill="currentColor"/>
          </svg>
        );

        announcement = {
          type: 'day-2',
          message: <>Ton accès expire demain. Continue d&apos;optimiser tes résultats sans interruption.</>,
          mobileMessage: "Expire demain",
          backgroundColor: '#f59e0b40',
          lightBackgroundColor: '#f59e0b99',
          color: '#ffffff',
          iconColor: '#fcd34d',
          icon: <AlertIcon />,
          buttonText: 'Continuer sans interruption',
          mobileButtonText: 'Continuer',
          buttonAction: 'pricing-modal',
          canDismiss: false,
          remainingTime: remainingMs,
        };
      } else {
        // Plus de 48h - Violet - Premium Actif
        const LightningIcon = () => (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
            <path d="M5.66953 9.91436L8.73167 5.77133C10.711 3.09327 11.7007 1.75425 12.6241 2.03721C13.5474 2.32018 13.5474 3.96249 13.5474 7.24712V7.55682C13.5474 8.74151 13.5474 9.33386 13.926 9.70541L13.946 9.72466C14.3327 10.0884 14.9492 10.0884 16.1822 10.0884C18.4011 10.0884 19.5106 10.0884 19.8855 10.7613C19.8917 10.7724 19.8977 10.7837 19.9036 10.795C20.2576 11.4784 19.6152 12.3475 18.3304 14.0857L15.2683 18.2287C13.2889 20.9067 12.2992 22.2458 11.3758 21.9628C10.4525 21.6798 10.4525 20.0375 10.4525 16.7528L10.4526 16.4433C10.4526 15.2585 10.4526 14.6662 10.074 14.2946L10.054 14.2754C9.6673 13.9117 9.05079 13.9117 7.81775 13.9117C5.59888 13.9117 4.48945 13.9117 4.1145 13.2387C4.10829 13.2276 4.10225 13.2164 4.09639 13.205C3.74244 12.5217 4.3848 11.6526 5.66953 9.91436Z" fill="currentColor"/>
          </svg>
        );

        announcement = {
          type: 'day-1',
          message: 'Premium actif — Atlas a repéré tes premières erreurs.',
          mobileMessage: 'Premium actif',
          backgroundColor: '#17171f',
          color: '#ffffff',
          iconColor: '#b5aeff',
          icon: <LightningIcon />,
          buttonText: 'Garder mes analyses',
          mobileButtonText: 'Garder',
          buttonAction: 'pricing-modal',
          canDismiss: true,
          remainingTime: remainingMs,
        };
      }

      setTrialAnnouncement(announcement);
    };

    // Mise à jour initiale
    updateCountdown();

    // Mettre à jour le countdown chaque seconde (calcul LOCAL, 0 requête Firestore)
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [user?.uid]);

  // Vérifier les annonces quand l'utilisateur se connecte
  useEffect(() => {
    const checkAnnouncements = async () => {
      if (!user?.uid) {
        setShowLightModeModal(false);
        setShowColorThemeModal(false);
        pendingColorThemeModal.current = false;
        lightModeModalCompleted.current = false;
        return;
      }

      // Éviter les vérifications multiples pour le même utilisateur
      if (checkedUserIds.has(user.uid)) return;

      try {
        // Vérifier si l'annonce du mode light doit être affichée
        const shouldShowLight = await announcementService.shouldShowLightModeAnnouncement(user.uid);
        
        // Vérifier si l'annonce du thème de couleurs doit être affichée
        // Note: l'affichage automatique est désactivé — c'est /demarrage qui déclenche ce modal (Option B)
        const shouldShowColor = await announcementService.shouldShowColorThemeAnnouncement(user.uid);
        
        // Le ColorThemeSelectionModal gère maintenant les 2 étapes en interne
        // On stocke juste si on doit afficher le modal light (qui sera l'étape 1)
        setShowLightModeModal(shouldShowLight);
        
        // Auto-affichage désactivé : c'est la page /demarrage qui déclenche le modal de personnalisation
        // if (shouldShowColor && !shouldShowLight) { ... }
        // else if (shouldShowLight) { ... }
        void shouldShowColor; // referenced to avoid TS unused-var warning

        // Marquer cet utilisateur comme vérifié
        setCheckedUserIds(prev => new Set([...prev, user.uid]));
      } catch (error) {
        // console.error supprimé
        // Marquer comme vérifié même en cas d'erreur pour éviter les boucles
        setCheckedUserIds(prev => new Set([...prev, user.uid]));
      }
    };

    checkAnnouncements();
  }, [user?.uid, checkedUserIds]);

  // Désactivé : l'affichage du modal couleur sur resize est géré par /demarrage (Option B)
  // useEffect resize supprimé pour éviter le déclenchement automatique

  const dismissLightModeAnnouncement = async () => {
    if (!user?.uid) return;

    try {
      await announcementService.dismissLightModeAnnouncement(user.uid);
      lightModeModalCompleted.current = true;
      setShowLightModeModal(false);
    } catch (error) {
      // console.error supprimé
    }
  };

  const markLightModeAnnouncementAsSeen = async () => {
    if (!user?.uid) return;

    try {
      await announcementService.markLightModeAnnouncementAsSeen(user.uid);
      lightModeModalCompleted.current = true;
      setShowLightModeModal(false);
    } catch (error) {
      // console.error supprimé
    }
  };

  const dismissColorThemeAnnouncement = async () => {
    if (!user?.uid) return;

    try {
      await announcementService.dismissColorThemeAnnouncement(user.uid);
      setShowColorThemeModal(false);
    } catch (error) {
      // console.error supprimé
    }
  };

  const markColorThemeAnnouncementAsSeen = async () => {
    if (!user?.uid) return;

    try {
      await announcementService.markColorThemeAnnouncementAsSeen(user.uid);
      setShowColorThemeModal(false);
    } catch (error) {
      // console.error supprimé
    }
  };

  // Fournir les données de démo quand le modal de thème de couleurs est affiché
  const demoData: DemoData | null = useMemo(() => {
    if (showColorThemeModal) {
      return getDemoPreviewData();
    }
    return null;
  }, [showColorThemeModal]);

  const value = {
    showLightModeModal,
    setShowLightModeModal,
    dismissLightModeAnnouncement,
    markLightModeAnnouncementAsSeen,
    showColorThemeModal,
    setShowColorThemeModal,
    dismissColorThemeAnnouncement,
    markColorThemeAnnouncementAsSeen,
    hideMainLayout: false,
    demoData,
    trialAnnouncement
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
}

export function useAnnouncement() {
  const context = useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error('useAnnouncement must be used within an AnnouncementProvider');
  }
  return context;
} 
